import { Capacitor } from '@capacitor/core';
import { getClientInstanceId } from './clientInstance';
import { reSyncAll, reSyncCollection } from './rxdb/replication';

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = 1000;
let everOpened = false;
let currentUserId: string | null = null;

function resolveBaseUrl(): string {
  if (Capacitor.isNativePlatform()) {
    const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
    if (configured && configured.length > 0) return configured;
    if (typeof window !== 'undefined') return window.location.origin;
  }
  return '';
}

function getWsUrl(token: string): string {
  const base = resolveBaseUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/realtime/connect?token=${encodeURIComponent(token)}`;
}

export async function startRealtimeSync(userId: string): Promise<void> {
  const { getFeatureFlag } = await import('./featureFlags');
  let enabled = await getFeatureFlag('real_time_sync');

  if (!enabled) {
    const { getSession, parseJwtClaimsForDisplay } = await import('./authStore');
    const session = await getSession();
    if (session?.token) {
      const parsed = parseJwtClaimsForDisplay(session.token);
      enabled = parsed?.normalized.real_time_sync ?? false;
    }
  }

  if (!enabled) return;
  currentUserId = userId;
  connect();
}

export function stopRealtimeSync(): void {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  if (ws) ws.close();
  ws = null;
  currentUserId = null;
  everOpened = false;
  retryDelay = 1000;
}

async function connect(): Promise<void> {
  const userId = currentUserId;
  if (!userId) return;

  const { getSession } = await import('./authStore');
  const session = await getSession();
  if (!session?.token) return;

  const url = getWsUrl(session.token);

  try {
    ws = new WebSocket(url);

    ws.onopen = () => {
      everOpened = true;
      retryDelay = 1000;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string);
        if (msg.type === 'data_changed' && currentUserId) {
          if (msg.originInstanceId === getClientInstanceId()) return;

          const collection = typeof msg.collection === 'string' ? msg.collection : null;
          if (collection) {
            reSyncCollection(currentUserId, collection);
          } else {
            reSyncAll(currentUserId);
          }
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      ws = null;
      if (everOpened && currentUserId) scheduleReconnect();
    };
  } catch {
    if (currentUserId) scheduleReconnect();
  }
}

function scheduleReconnect(): void {
  reconnectTimer = setTimeout(() => {
    connect();
    retryDelay = Math.min(retryDelay * 2, 30_000);
  }, retryDelay);
}
